import { useEffect, useState } from "react";
import {
  Modal,
  Table,
  Tag,
  Spin,
  Descriptions,
  Statistic,
  Row,
  Col,
  Divider,
  Card,
  Empty,
} from "antd";
import dayjs from "dayjs";

import { postAPI } from "../../../utils/common";
import { VENDOR_BILLING_DETAIL } from "../../../constants/api";
import { BILLING_CYCLE, CURRENCY, HOTEL_ID_SERIES } from "../../../constants/constent";

/**
 * Reusable "Vendor Billing & Finance Details" modal.
 *
 * Fetches a vendor's full billing + finance snapshot (vendors/billing-detail)
 * and renders the finance summary, plan & billing descriptions and the bill
 * history. Self-contained: give it a vendorId and open/onClose.
 *
 * Usage:
 *   <VendorBillingDetailModal open={open} vendorId={id} onClose={fn} />
 */
function VendorBillingDetailModal({ open, vendorId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (open && vendorId) {
      setDetail(null);
      setLoading(true);
      postAPI(
        VENDOR_BILLING_DETAIL,
        { id: vendorId },
        (res) => {
          if (res.status) setDetail(res.data);
          setLoading(false);
        },
        () => setLoading(false),
      );
    }
  }, [open, vendorId]);

  /* ================= HELPERS ================= */

  const getBillingCycleLabel = (value) => {
    const match = BILLING_CYCLE.find((c) => c.value === Number(value));
    return match ? match.label : "-";
  };

  const money = (v) =>
    v === null || v === undefined || v === ""
      ? "-"
      : `${CURRENCY}${Number(v).toLocaleString("en-IN")}`;

  const fmtDate = (d) => (d ? dayjs(d).format("DD-MM-YYYY") : "-");

  const renderVendorStatus = (value) => {
    if (value === "active") return <Tag color="green">Active</Tag>;
    if (value === "inactive") return <Tag color="red">Inactive</Tag>;
    if (value === "pending") return <Tag color="orange">Pending</Tag>;
    return <Tag>NA</Tag>;
  };

  const renderBillStatus = (value) => {
    const map = {
      paid: "green",
      partial: "gold",
      pending: "orange",
      cancelled: "red",
    };
    return (
      <Tag color={map[value] || "default"}>{(value || "-").toUpperCase()}</Tag>
    );
  };

  const billColumns = [
    { title: "Bill No", dataIndex: "bill_no" },
    { title: "Invoice No", dataIndex: "invoice_no" },
    {
      title: "Type",
      dataIndex: "bill_type",
      render: (t) => <Tag>{(t || "-").toUpperCase()}</Tag>,
    },
    { title: "Generated", dataIndex: "bill_gen_date", render: (d) => fmtDate(d) },
    { title: "Period End", dataIndex: "bill_end_date", render: (d) => fmtDate(d) },
    { title: "Amount", dataIndex: "bill_amount", render: (v) => money(v) },
    { title: "Paid", dataIndex: "total_paid_amount", render: (v) => money(v) },
    { title: "Due", dataIndex: "total_rest_amount", render: (v) => money(v) },
    {
      title: "Status",
      dataIndex: "bill_status",
      render: (s) => renderBillStatus(s),
    },
  ];

  const renderDetail = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      );
    }
    if (!detail) return <Empty description="No data" />;

    const { vendor, plan, summary } = detail;

    return (
      <>
        {/* Finance summary */}
        <Row gutter={16}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Total Billed"
                value={summary.total_billed}
                prefix={CURRENCY}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Total Paid"
                value={summary.total_paid}
                prefix={CURRENCY}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Outstanding"
                value={summary.total_outstanding}
                prefix={CURRENCY}
                valueStyle={{
                  color: summary.total_outstanding > 0 ? "#cf1322" : "#3f8600",
                }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic title="Total Bills" value={summary.bills_count} />
            </Card>
          </Col>
        </Row>

        <Divider orientation="left">Plan & Billing</Divider>
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Hotel">
            {HOTEL_ID_SERIES + (vendor?.hotel_id ?? "")} — {vendor?.hotel_name}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            {renderVendorStatus(vendor?.status)}
          </Descriptions.Item>
          <Descriptions.Item label="Active Plan">
            {plan?.plan_name || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Monthly Plan Amount">
            {money(vendor?.active_plan_amount)}
          </Descriptions.Item>
          <Descriptions.Item label="Billing Cycle">
            {getBillingCycleLabel(vendor?.billing_cycle)}
          </Descriptions.Item>
          <Descriptions.Item label="Auto Generation">
            {vendor?.auto_bill_generation ? (
              <Tag color="green">On</Tag>
            ) : (
              <Tag color="red">Off</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Expected Live Date">
            {fmtDate(vendor?.expected_live_date)}
          </Descriptions.Item>
          <Descriptions.Item label="Billing Start Date">
            {fmtDate(vendor?.billing_start_date)}
          </Descriptions.Item>
          <Descriptions.Item label="Last Billed On">
            {fmtDate(detail.last_billed_on)}
          </Descriptions.Item>
          <Descriptions.Item label="Current Period End">
            {fmtDate(detail.last_bill_end_date)}
          </Descriptions.Item>
          <Descriptions.Item label="Next Billing Date">
            {fmtDate(vendor?.next_billing_date)}
          </Descriptions.Item>
          <Descriptions.Item label="Bill Breakdown">
            <Tag color="green">Paid {summary.paid_count}</Tag>
            <Tag color="orange">Pending {summary.pending_count}</Tag>
            <Tag color="gold">Partial {summary.partial_count}</Tag>
            <Tag color="red">Cancelled {summary.cancelled_count}</Tag>
          </Descriptions.Item>
        </Descriptions>

        <Divider orientation="left">Bill History</Divider>
        <Table
          rowKey="id"
          size="small"
          columns={billColumns}
          dataSource={detail.bills || []}
          pagination={{ pageSize: 8 }}
          scroll={{ x: 1000 }}
        />
      </>
    );
  };

  return (
    <Modal
      title="Vendor Billing & Finance Details"
      width={1400}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      {renderDetail()}
    </Modal>
  );
}

export default VendorBillingDetailModal;
